import { API_URL_ADMIN } from '~/config/config';
import { showCollectItemPopup } from '../controllers/popups/CollectItemPopup';
import MapUtils from './MapUtils';
import { LayerStyles, defaultStyles } from '../panels/GeocollectDatatablePanel/tabs/LayerStyleModal/types';
import { getHashColor } from '~/utils/hashStringUtil';

export const COLLECT_ITEMS_SOURCE_ID = 'collect-items-source';
export const COLLECT_ITEMS_LAYER_ID_PREFIX = 'collect-items-layer';

const collectItemsEvents: any = {};
let autoRefreshInterval: NodeJS.Timeout | null = null;
let autoRefreshEnabled = false;
let currentProjectId: number | undefined = undefined;
let cachedMembers: any[] = [];
let cachedForms: any[] = [];

// Default styles
let currentStyles: LayerStyles = { ...defaultStyles };

const CollectItemsMapUtils = {
  addToMap: (projectId?: number) => {
    const map = window.map;
    if (!map) {
      return;
    }

    // Store current project ID for auto-refresh
    currentProjectId = projectId;

    // Remove existing layers and source if they exist
    const existingLayers =
      map.getStyle()?.layers?.filter((layer: any) => layer.id.startsWith(COLLECT_ITEMS_LAYER_ID_PREFIX)) || [];

    existingLayers.forEach((layer: any) => {
      if (map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
      }
    });

    if (map.getSource(COLLECT_ITEMS_SOURCE_ID)) {
      map.removeSource(COLLECT_ITEMS_SOURCE_ID);
    }

    // Add vector tile source for collect items with project filter
    const url = projectId
      ? `${API_URL_ADMIN}/api/collect-items-ext/tiles/{z}/{x}/{y}.pbf?project_id=${projectId}`
      : `${API_URL_ADMIN}/api/collect-items-ext/tiles/{z}/{x}/{y}.pbf`;

    map.addSource(COLLECT_ITEMS_SOURCE_ID, {
      type: 'vector',
      tiles: [url],
      maxzoom: 22,
    });

    // Add all layers
    CollectItemsMapUtils._addLayers();

    // Add event listeners
    CollectItemsMapUtils._addEventListeners();

    // Start auto-refresh if enabled
    if (autoRefreshEnabled) {
      CollectItemsMapUtils.startAutoRefresh();
    }
  },

  _addLayers: () => {
    const map = window.map;
    if (!map) return;

    const layerIds: string[] = [];

    // 1. Polygon/Fill layer (for areas/vùng)
    const fillLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-fill`;
    map.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: COLLECT_ITEMS_SOURCE_ID,
      'source-layer': 'collect_item',
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'fill-color': CollectItemsMapUtils._getColorExpression(currentStyles.polygon.fillColor),
        'fill-opacity': currentStyles.polygon.fillOpacity,
      },
    });
    layerIds.push(fillLayerId);

    // 2. Polygon outline layer (for area borders)
    const strokeLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-stroke`;
    map.addLayer({
      id: strokeLayerId,
      type: 'line',
      source: COLLECT_ITEMS_SOURCE_ID,
      'source-layer': 'collect_item',
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'line-color': CollectItemsMapUtils._getColorExpression(currentStyles.polygon.strokeColor),
        'line-width': currentStyles.polygon.strokeWidth,
        'line-opacity': currentStyles.polygon.strokeOpacity,
        ...(currentStyles.polygon.dashPattern && {
          'line-dasharray': currentStyles.polygon.dashPattern,
        }),
      },
    });
    layerIds.push(strokeLayerId);

    // 3. Line layer (for lines/đường)
    const lineLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-line`;
    map.addLayer({
      id: lineLayerId,
      type: 'line',
      source: COLLECT_ITEMS_SOURCE_ID,
      'source-layer': 'collect_item',
      filter: ['==', '$type', 'LineString'],
      paint: {
        'line-color': CollectItemsMapUtils._getColorExpression(currentStyles.line.color),
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, currentStyles.line.width * 0.5, 16, currentStyles.line.width, 22, currentStyles.line.width * 1.5],
        'line-opacity': currentStyles.line.opacity,
        ...(currentStyles.line.dashPattern && {
          'line-dasharray': currentStyles.line.dashPattern,
        }),
      },
    });
    layerIds.push(lineLayerId);

    // 4. Point layer (for points/điểm)
    const pointLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-point`;
    map.addLayer({
      id: pointLayerId,
      type: 'circle',
      source: COLLECT_ITEMS_SOURCE_ID,
      'source-layer': 'collect_item',
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-color': CollectItemsMapUtils._getColorExpression(currentStyles.point.color),
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, currentStyles.point.radius * 0.5, 16, currentStyles.point.radius, 22, currentStyles.point.radius * 1.5],
        'circle-stroke-width': currentStyles.point.strokeWidth,
        'circle-stroke-color': currentStyles.point.strokeColor,
        'circle-opacity': currentStyles.point.opacity,
      },
    });
    layerIds.push(pointLayerId);

    // 5. Label layer for Points (for displaying name text)
    const labelPointLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-labels-point`;
    map.addLayer({
      id: labelPointLayerId,
      type: 'symbol',
      source: COLLECT_ITEMS_SOURCE_ID,
      'source-layer': 'collect_item',
      filter: ['==', '$type', 'Point'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': currentStyles.labels.size,
        'text-offset': [0, 1.5],
        'text-anchor': 'top',
        'text-allow-overlap': false,
        'symbol-placement': 'point',
        'visibility': currentStyles.labels.enabled ? 'visible' : 'none',
      },
      paint: {
        'text-color': currentStyles.labels.color,
        'text-halo-color': currentStyles.labels.haloColor,
        'text-halo-width': currentStyles.labels.haloWidth,
      },
      minzoom: currentStyles.labels.minZoom,
    });
    layerIds.push(labelPointLayerId);

    // 6. Label layer for LineStrings (centered on line)
    const labelLineLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-labels-line`;
    map.addLayer({
      id: labelLineLayerId,
      type: 'symbol',
      source: COLLECT_ITEMS_SOURCE_ID,
      'source-layer': 'collect_item',
      filter: ['==', '$type', 'LineString'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': currentStyles.labels.size,
        'text-offset': [0, 1],
        'text-allow-overlap': false,
        'symbol-placement': 'line-center',
        'visibility': currentStyles.labels.enabled ? 'visible' : 'none',
      },
      paint: {
        'text-color': currentStyles.labels.color,
        'text-halo-color': currentStyles.labels.haloColor,
        'text-halo-width': currentStyles.labels.haloWidth,
      },
      minzoom: currentStyles.labels.minZoom,
    });
    layerIds.push(labelLineLayerId);

    // 7. Label layer for Polygons (centered in polygon)
    const labelPolygonLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-labels-polygon`;
    map.addLayer({
      id: labelPolygonLayerId,
      type: 'symbol',
      source: COLLECT_ITEMS_SOURCE_ID,
      'source-layer': 'collect_item',
      filter: ['==', '$type', 'Polygon'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': currentStyles.labels.size,
        'text-allow-overlap': false,
        'symbol-placement': 'point',
        'visibility': currentStyles.labels.enabled ? 'visible' : 'none',
      },
      paint: {
        'text-color': currentStyles.labels.color,
        'text-halo-color': currentStyles.labels.haloColor,
        'text-halo-width': currentStyles.labels.haloWidth,
      },
      minzoom: currentStyles.labels.minZoom,
    });
    layerIds.push(labelPolygonLayerId);

    return layerIds;
  },

  _addEventListeners: () => {
    const map = window.map;
    if (!map) return;

    // Remove old event listeners first
    const oldEvents = collectItemsEvents['collect-items'];
    if (oldEvents) {
      map.off('mousemove', oldEvents.onMouseMove);
      map.off('click', oldEvents.onClick);
    }

    // Get current layer IDs dynamically each call
    const getLayerIds = () =>
      map.getStyle()?.layers
        ?.filter((layer: any) => layer.id.startsWith(COLLECT_ITEMS_LAYER_ID_PREFIX))
        .map((layer: any) => layer.id) || [];

    // Add hover effect
    const onMouseMove = (e: any) => {
      // Add buffer for easier hovering on LineString features
      const bufferRadius = 25; // 25px buffer for hover detection
      let features: any[] = [];
      try {
        const layerIds = getLayerIds();
        if (layerIds.length === 0) return;

        features = map.queryRenderedFeatures(
          [
            [e.point.x - bufferRadius, e.point.y - bufferRadius],
            [e.point.x + bufferRadius, e.point.y + bufferRadius],
          ],
          { layers: layerIds },
        );
      } catch (err: any) {
        // Layer may not exist in current style (e.g., after basemap change)
        if (err.message && err.message.includes('does not exist')) {
          return;
        }
        console.warn('queryRenderedFeatures error in onMouseMove:', err);
        return;
      }

      if (features.length) {
        map.getCanvas().style.cursor = 'pointer';
      } else {
        map.getCanvas().style.cursor = '';
      }
    };

    // Add click handler
    const onClick = (e: any) => {
      let features: any[] = [];
      try {
        const layerIds = getLayerIds();
        if (layerIds.length === 0) return;

        // Add buffer for easier clicking on LineString features
        const bufferRadius = 30; // 30px buffer around click point
        features = map.queryRenderedFeatures(
          [
            [e.point.x - bufferRadius, e.point.y - bufferRadius],
            [e.point.x + bufferRadius, e.point.y + bufferRadius],
          ],
          { layers: layerIds },
        );
      } catch (err: any) {
        // Layer may not exist in current style (e.g., after basemap change)
        if (err.message && err.message.includes('does not exist')) {
          return;
        }
        console.warn('queryRenderedFeatures error in onClick:', err);
        return;
      }

      if (features.length) {
        const feature = features[0];

        // Ensure lngLat is properly formatted
        const lngLat = e.lngLat || map.unproject(e.point);

        // Note: Vector tile properties should include collect_form_uuid
        // If not available, you need to update backend to include it in the tiles response
        showCollectItemPopup({
          title: feature.properties?.name || 'Dữ liệu thu thập',
          lngLat: lngLat,
          properties: feature.properties,
        });

        // Highlight the clicked feature
        const featureCollection = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: feature.properties,
              geometry: feature.geometry,
            },
          ],
        };
        MapUtils.highlightFeatureCollection(featureCollection as any);
      } else {
        MapUtils.clearHighlight();
      }
    };

    // Attach event listeners
    map.on('mousemove', onMouseMove);
    map.on('click', onClick);

    // Store events for cleanup
    collectItemsEvents['collect-items'] = {
      onMouseMove,
      onClick,
    };
  },

  startAutoRefresh: (intervalSeconds: number = 30) => {
    // Stop existing interval
    CollectItemsMapUtils.stopAutoRefresh();

    autoRefreshEnabled = true;
    autoRefreshInterval = setInterval(() => {
      if (window.map && currentProjectId && CollectItemsMapUtils.isLayerVisible()) {
        console.log('Auto-refreshing collect items data...');
        // Use the same refresh logic as manual refresh button
        CollectItemsMapUtils.refreshData();
      }
    }, intervalSeconds * 1000);

    console.log(`Auto-refresh started with ${intervalSeconds}s interval`);
  },

  stopAutoRefresh: () => {
    if (autoRefreshInterval) {
      clearInterval(autoRefreshInterval);
      autoRefreshInterval = null;
    }
    autoRefreshEnabled = false;
    console.log('Auto-refresh stopped');
  },

  setAutoRefreshEnabled: (enabled: boolean, intervalSeconds?: number) => {
    if (enabled) {
      CollectItemsMapUtils.startAutoRefresh(intervalSeconds);
    } else {
      CollectItemsMapUtils.stopAutoRefresh();
    }
  },

  isAutoRefreshEnabled: (): boolean => {
    return autoRefreshEnabled;
  },

  refreshData: () => {
    const map = window.map;
    if (!map || !currentProjectId) {
      return;
    }

    try {
      const timestamp = Date.now();
      const newUrl = `${API_URL_ADMIN}/api/collect-items-ext/tiles/{z}/{x}/{y}.pbf?project_id=${currentProjectId}&_t=${timestamp}`;

      // Method 1: Try to update source tiles directly (cleanest approach)
      const source = map.getSource(COLLECT_ITEMS_SOURCE_ID) as any;
      if (source && source.setTiles) {
        source.setTiles([newUrl]);
        return;
      }

      // Method 2: Update source._options.tiles and reload
      if (source && source._options) {
        source._options.tiles = [newUrl];
        if (source.reload) {
          source.reload();
          return;
        }
      }

      // Method 3: Fallback - temporarily hide layers, update source, show layers
      CollectItemsMapUtils._smoothSourceUpdate(newUrl);

    } catch (error) {
      console.error('Error refreshing collect items data:', error);
    }
  },

  _smoothSourceUpdate: (newUrl: string) => {
    const map = window.map;
    if (!map) return;

    // Get all layer IDs
    const layerIds = [
      `${COLLECT_ITEMS_LAYER_ID_PREFIX}-fill`,
      `${COLLECT_ITEMS_LAYER_ID_PREFIX}-stroke`,
      `${COLLECT_ITEMS_LAYER_ID_PREFIX}-line`,
      `${COLLECT_ITEMS_LAYER_ID_PREFIX}-point`,
      `${COLLECT_ITEMS_LAYER_ID_PREFIX}-labels-point`,
      `${COLLECT_ITEMS_LAYER_ID_PREFIX}-labels-line`,
      `${COLLECT_ITEMS_LAYER_ID_PREFIX}-labels-polygon`,
    ];

    // Store current visibility states
    const visibilityStates: { [key: string]: string } = {};
    layerIds.forEach(layerId => {
      if (map.getLayer(layerId)) {
        visibilityStates[layerId] = map.getLayoutProperty(layerId, 'visibility') || 'visible';
        // Temporarily hide layers
        map.setLayoutProperty(layerId, 'visibility', 'none');
      }
    });

    // Small delay to let hide take effect
    setTimeout(() => {
      // Now we can safely remove the source
      if (map.getSource(COLLECT_ITEMS_SOURCE_ID)) {
        map.removeSource(COLLECT_ITEMS_SOURCE_ID);
      }

      // Add new source
      map.addSource(COLLECT_ITEMS_SOURCE_ID, {
        type: 'vector',
        tiles: [newUrl],
        maxzoom: 22,
      });

      // Re-add layers with same styles
      CollectItemsMapUtils._addLayers();

      // Restore visibility states
      setTimeout(() => {
        Object.entries(visibilityStates).forEach(([layerId, visibility]) => {
          if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', visibility);
          }
        });
      }, 50);
    }, 100);
  },

  removeFromMap: () => {
    const map = window.map;
    if (!map) {
      return;
    }

    // Stop auto-refresh when removing layer
    CollectItemsMapUtils.stopAutoRefresh();

    // Remove all collect items layers
    const existingLayers =
      map.getStyle()?.layers?.filter((layer: any) => layer.id.startsWith(COLLECT_ITEMS_LAYER_ID_PREFIX)) || [];

    existingLayers.forEach((layer: any) => {
      if (map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
      }
    });

    // Remove source
    if (map.getSource(COLLECT_ITEMS_SOURCE_ID)) {
      map.removeSource(COLLECT_ITEMS_SOURCE_ID);
    }

    // Remove event listeners
    const events = collectItemsEvents['collect-items'];
    if (events) {
      map.off('mousemove', events.onMouseMove);
      map.off('click', events.onClick);
      delete collectItemsEvents['collect-items'];
    }

    return existingLayers.length > 0;
  },

  moveLayerToTop: () => {
    const map = window.map;
    console.log('Moving collect items layers to top');
    if (!map) return;
    console.log('Moving collect items layers to top');


    const layerSuffixes = ['-fill', '-stroke', '-line', '-point', '-labels-point', '-labels-line', '-labels-polygon'];
    layerSuffixes.forEach((suffix) => {
      const layerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}${suffix}`;
      console.log(`Attempting to move layer ${layerId} to top`);
      if (map.getLayer(layerId)) {
        console.log(`Moving layer ${layerId} to top`);
        map.moveLayer(layerId);
      }
    });
  },

  isLayerVisible: (): boolean => {
    const map = window.map;
    if (!map) {
      return false;
    }

    // Check if any collect items layer exists and is visible
    const pointLayer = map.getLayer(`${COLLECT_ITEMS_LAYER_ID_PREFIX}-point`);
    if (!pointLayer) return false;

    const visibility = map.getLayoutProperty(`${COLLECT_ITEMS_LAYER_ID_PREFIX}-point`, 'visibility');
    return visibility !== 'none';
  },

  toggleLayer: () => {
    const map = window.map;
    if (!map) {
      return;
    }

    const isVisible = CollectItemsMapUtils.isLayerVisible();
    const visibility = isVisible ? 'none' : 'visible';

    // Toggle visibility for all layer types
    const layerSuffixes = ['-fill', '-stroke', '-line', '-point', '-labels-point', '-labels-line', '-labels-polygon'];
    layerSuffixes.forEach((suffix) => {
      const layerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}${suffix}`;
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility);
      }
    });
  },

  setMembers: (members: any[]) => {
    cachedMembers = members || [];
    if ((currentStyles.mode === 'collector' || !currentStyles.mode) && CollectItemsMapUtils.isLayerVisible()) {
      CollectItemsMapUtils._updateLayerColors();
    }
  },

  getMembers: (): any[] => {
    return cachedMembers;
  },

  setForms: (forms: any[]) => {
    cachedForms = forms || [];
    if (currentStyles.mode === 'form' && CollectItemsMapUtils.isLayerVisible()) {
      CollectItemsMapUtils._updateLayerColors();
    }
  },

  getForms: (): any[] => {
    return cachedForms;
  },

  _getColorExpression: (defaultColor: string): any => {
    const mode = currentStyles.mode || 'collector';

    if (mode === 'form') {
      const pairs: any[] = [];
      const assignedColors = currentStyles.formColors || {};

      // Match on collect_form_uuid
      cachedForms.forEach((f: any) => {
        const uuid = f?.uuid;
        if (uuid) {
          const color = assignedColors[uuid] || getHashColor(f.name || uuid).backgroundColor;
          pairs.push(uuid, color);
        }
      });

      // Also include any uuids from assignedColors not in cachedForms
      Object.entries(assignedColors).forEach(([uuid, color]) => {
        if (!pairs.includes(uuid)) {
          pairs.push(uuid, color);
        }
      });

      if (pairs.length >= 2) {
        // MapLibre expression: ['match', ['to-string', ['coalesce', ['get', 'collect_form_uuid'], '']], ...pairs, defaultColor]
        return ['match', ['to-string', ['coalesce', ['get', 'collect_form_uuid'], '']], ...pairs, defaultColor];
      }
      return defaultColor;
    }

    if (mode === 'collector') {
      const pairs: any[] = [];
      const assignedColors = currentStyles.collectorColors || {};

      // Match on user_id
      cachedMembers.forEach((m: any) => {
        const uid = m?.user?.id;
        if (uid) {
          const color = assignedColors[uid] || getHashColor(m.user.username || uid.toString()).backgroundColor;
          pairs.push(uid, color);
        }
      });

      // Also include any userIds from assignedColors not in cachedMembers
      Object.entries(assignedColors).forEach(([uidStr, color]) => {
        const uid = Number(uidStr);
        if (!isNaN(uid) && !pairs.includes(uid)) {
          pairs.push(uid, color);
        }
      });

      if (pairs.length >= 2) {
        // MapLibre expression: ['match', ['to-number', ['coalesce', ['get', 'user_id'], 0]], ...pairs, defaultColor]
        return ['match', ['to-number', ['coalesce', ['get', 'user_id'], 0]], ...pairs, defaultColor];
      }
      return defaultColor;
    }

    if (mode === 'date') {
      const now = Math.floor(Date.now() / 1000);
      const t_24h = now - 86400;
      const t_3d = now - 3 * 86400;
      const t_7d = now - 7 * 86400;
      const t_14d = now - 14 * 86400;
      const t_30d = now - 30 * 86400;

      // ['step', input, default_output, stop_1, output_1, ...]
      return [
        'step',
        ['to-number', ['coalesce', ['get', 'created_at_ts'], 0]],
        '#ef4444',
        t_30d, '#f97316',
        t_14d, '#eab308',
        t_7d, '#10b981',
        t_3d, '#3b82f6',
        t_24h, '#8b5cf6',
      ];
    }

    return defaultColor;
  },

  _updateLayerColors: () => {
    const map = window.map;
    if (!map) return;

    const fillLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-fill`;
    const strokeLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-stroke`;
    const lineLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-line`;
    const pointLayerId = `${COLLECT_ITEMS_LAYER_ID_PREFIX}-point`;

    if (map.getLayer(fillLayerId)) {
      map.setPaintProperty(
        fillLayerId,
        'fill-color',
        CollectItemsMapUtils._getColorExpression(currentStyles.polygon.fillColor)
      );
    }
    if (map.getLayer(strokeLayerId)) {
      map.setPaintProperty(
        strokeLayerId,
        'line-color',
        CollectItemsMapUtils._getColorExpression(currentStyles.polygon.strokeColor)
      );
    }
    if (map.getLayer(lineLayerId)) {
      map.setPaintProperty(
        lineLayerId,
        'line-color',
        CollectItemsMapUtils._getColorExpression(currentStyles.line.color)
      );
    }
    if (map.getLayer(pointLayerId)) {
      map.setPaintProperty(
        pointLayerId,
        'circle-color',
        CollectItemsMapUtils._getColorExpression(currentStyles.point.color)
      );
    }
  },

  updateStyles: (newStyles: LayerStyles) => {
    currentStyles = { ...newStyles };
    // Store styles in localStorage for persistence
    localStorage.setItem('collectItemsLayerStyles', JSON.stringify(currentStyles));
    CollectItemsMapUtils._updateLayerColors();
  },

  getStyles: (): LayerStyles => {
    // Load styles from localStorage if available
    const savedStyles = localStorage.getItem('collectItemsLayerStyles');
    if (savedStyles) {
      try {
        const parsed = JSON.parse(savedStyles);
        currentStyles = {
          ...defaultStyles,
          ...parsed,
          point: { ...defaultStyles.point, ...(parsed.point || {}) },
          line: { ...defaultStyles.line, ...(parsed.line || {}) },
          polygon: { ...defaultStyles.polygon, ...(parsed.polygon || {}) },
          labels: { ...defaultStyles.labels, ...(parsed.labels || {}) },
          mode: parsed.mode || defaultStyles.mode,
          collectorColors: parsed.collectorColors || defaultStyles.collectorColors,
          formColors: parsed.formColors || defaultStyles.formColors,
        };
      } catch (error) {
        console.error('Error loading saved layer styles:', error);
      }
    }
    return currentStyles;
  },
};

// Initialize styles from localStorage on load
CollectItemsMapUtils.getStyles();

export default CollectItemsMapUtils;
